import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import {
  LayoutDashboard, Handshake, FileText, Users, Wrench,
  LogOut, UserCircle, BarChart3, CalendarDays, PhoneCall, UserCog,
  Menu, X, ArrowLeft, Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import '@/index.admin.css';
import AdminPwaHead from '@/components/admin/AdminPwaHead';
import AdminPwaInstallBanner from '@/components/admin/AdminPwaInstallBanner';
import AdminPwaReloadPrompt from '@/components/admin/AdminPwaReloadPrompt';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const NAV_ITEMS = [
  { path: '/admin', label: 'Dashboard', icon: LayoutDashboard, exact: true, section: 'dashboard' },
  { path: '/admin/deals', label: 'Deals Hub', icon: Handshake, section: 'deals' },
  { path: '/admin/documents', label: 'Documents', icon: FileText, section: 'documents' },
  { path: '/admin/operations', label: 'Operations', icon: BarChart3, section: 'operations' },
  { path: '/admin/leads', label: 'Track Leads', icon: Users, section: 'leads' },
  { path: '/admin/service-requests', label: 'Service', icon: Wrench, section: 'service' },
  { path: '/admin/imagine', label: 'Imagine', icon: Sparkles, section: 'imagine' },
  { path: '/admin/outreach', label: 'Outreach Agent', icon: PhoneCall, section: 'outreach' },
  { path: '/admin/schedule', label: 'Schedule', icon: CalendarDays, section: 'schedule' },
  { path: '/admin/users', label: 'Users', icon: UserCog, section: 'user_management' },
];

function chromeForPath(pathname) {
  if (pathname === '/admin') return { title: 'Dashboard' };
  if (pathname.startsWith('/admin/vehicles/')) return { title: 'Vehicle', backTo: '/admin' };
  if (pathname.startsWith('/admin/sold-vehicles/')) return { title: 'Sold Vehicle', backTo: '/admin/sold-vehicles' };
  if (pathname === '/admin/sold-vehicles') return { title: 'Sold Vehicles', backTo: '/admin' };
  if (/^\/admin\/deals\/[^/]+$/.test(pathname)) return { title: 'Deal', backTo: '/admin/deals' };
  if (pathname === '/admin/deals') return { title: 'Deals Hub' };
  if (pathname === '/admin/documents') return { title: 'Documents' };
  if (pathname === '/admin/operations') return { title: 'Operations' };
  if (/^\/admin\/leads\/[^/]+$/.test(pathname)) return { title: 'Lead', backTo: '/admin/leads' };
  if (pathname === '/admin/leads') return { title: 'Track Leads' };
  if (/^\/admin\/service-requests\/[^/]+$/.test(pathname)) return { title: 'Service Request', backTo: '/admin/service-requests' };
  if (pathname === '/admin/service-requests') return { title: 'Service' };
  if (pathname === '/admin/imagine') return { title: 'Imagine' };
  if (pathname === '/admin/outreach') return { title: 'Outreach Agent' };
  if (pathname === '/admin/schedule') return { title: 'Schedule' };
  if (pathname === '/admin/users') return { title: 'Users' };
  if (pathname === '/admin/profile') return { title: 'Profile' };
  return { title: 'Admin Portal' };
}

const PATH_SECTION = [
  { prefix: '/admin/users', section: 'user_management' },
  { prefix: '/admin/vehicles', section: 'dashboard' },
  { prefix: '/admin/sold-vehicles', section: 'dashboard' },
  { prefix: '/admin/deals', section: 'deals' },
  { prefix: '/admin/documents', section: 'documents' },
  { prefix: '/admin/operations', section: 'operations' },
  { prefix: '/admin/leads', section: 'leads' },
  { prefix: '/admin/service-requests', section: 'service' },
  { prefix: '/admin/imagine', section: 'imagine' },
  { prefix: '/admin/outreach', section: 'outreach' },
  { prefix: '/admin/schedule', section: 'schedule' },
  { prefix: '/admin', section: 'dashboard', exact: true },
];

export default function AdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { token, logout, isAuthenticated, loading: authLoading, hasSection, user, firstAllowedPath } = useAuth();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!mobileNavOpen) return undefined;
    const onKey = (event) => {
      if (event.key === 'Escape') setMobileNavOpen(false);
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [mobileNavOpen]);

  React.useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/admin/login');
    }
  }, [isAuthenticated, authLoading, navigate]);

  // Route-level section guard
  React.useEffect(() => {
    if (authLoading || !isAuthenticated || !user) return;
    const path = location.pathname;
    let required = null;
    for (const rule of PATH_SECTION) {
      if (rule.exact) {
        if (path === rule.prefix) {
          required = rule.section;
          break;
        }
      } else if (path === rule.prefix || path.startsWith(rule.prefix + '/')) {
        required = rule.section;
        break;
      }
    }
    if (required && !hasSection(required)) {
      toast.error("You don't have access to this section");
      navigate(firstAllowedPath());
    }
  }, [location.pathname, authLoading, isAuthenticated, user, hasSection, navigate, firstAllowedPath]);

  const handleLogout = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/admin/logout`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      logout();
      toast.success('Logged out successfully');
      navigate('/');
    }
  };

  const isActive = (item) => {
    if (item.exact) return location.pathname === item.path;
    return location.pathname.startsWith(item.path);
  };

  const visibleNav = NAV_ITEMS.filter((item) => hasSection(item.section));

  if (authLoading || !isAuthenticated) return null;

  const go = (path) => {
    setMobileNavOpen(false);
    navigate(path);
  };

  const sidebar = (
    <>
      <div className="px-5 py-5 border-b border-white/10 flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h2 className="text-base font-['Unbounded'] font-bold text-white tracking-tight">
            Drive Now
          </h2>
          <p className="text-[11px] font-['Manrope'] text-white/30 mt-0.5">Admin Portal</p>
          {user?.name && (
            <p className="text-[11px] font-['Manrope'] text-white/50 mt-2 truncate" title={user.email}>
              {user.name}
            </p>
          )}
          {user?.role_label && (
            <p className="text-[10px] font-['Manrope'] text-blue-400/80 mt-0.5 truncate">
              {user.role_label}
            </p>
          )}
        </div>
        <button
          type="button"
          className="lg:hidden inline-flex items-center justify-center min-h-11 min-w-11 p-2 text-white/50 hover:text-white"
          aria-label="Close menu"
          onClick={() => setMobileNavOpen(false)}
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      <nav className="flex-1 py-3 px-2 space-y-0.5 overflow-y-auto" data-testid="admin-nav">
        {visibleNav.map((item) => {
          const active = isActive(item);
          const Icon = item.icon;
          return (
            <button
              key={item.path}
              type="button"
              onClick={() => go(item.path)}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              className={`w-full flex items-center gap-2.5 px-3 min-h-11 py-2 text-[13px] font-['Manrope'] transition-all ${
                active
                  ? 'bg-blue-500/15 text-blue-400 border-l-2 border-blue-400 pl-[10px]'
                  : 'text-white/50 hover:bg-white/5 hover:text-white/80 border-l-2 border-transparent'
              }`}
            >
              <Icon className="w-4 h-4 shrink-0" />
              {item.label}
            </button>
          );
        })}
      </nav>

      <div className="px-2 py-3 border-t border-white/10 space-y-0.5">
        <button
          type="button"
          onClick={() => go('/admin/profile')}
          data-testid="nav-profile"
          className={`w-full flex items-center gap-2.5 px-3 min-h-11 py-2 font-['Manrope'] text-[13px] transition-all ${
            location.pathname === '/admin/profile'
              ? 'bg-blue-500/15 text-blue-400'
              : 'text-white/30 hover:text-white/60 hover:bg-white/5'
          }`}
        >
          <UserCircle className="w-4 h-4" />
          Profile
        </button>
        <button
          type="button"
          onClick={handleLogout}
          data-testid="nav-logout"
          className="w-full flex items-center gap-2.5 px-3 min-h-11 py-2 text-red-400/60 hover:text-red-400 hover:bg-red-500/10 font-['Manrope'] text-[13px] transition-all"
        >
          <LogOut className="w-4 h-4" />
          Logout
        </button>
      </div>
    </>
  );

  const chrome = chromeForPath(location.pathname);

  return (
    <div className="admin-shell min-h-[100dvh] bg-[#050505] flex overflow-x-hidden" data-testid="admin-layout">
      {mobileNavOpen && (
        <button
          type="button"
          aria-label="Close menu"
          className="fixed inset-0 z-[60] bg-black/70 lg:hidden"
          onClick={() => setMobileNavOpen(false)}
        />
      )}

      <aside
        className={`w-56 bg-[#0a0a0a] border-r border-white/10 flex flex-col fixed h-[100dvh] z-[70] pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)] transition-transform duration-200 ease-out ${
          mobileNavOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:translate-x-0`}
      >
        {sidebar}
      </aside>

      <div className="flex-1 min-h-[100dvh] min-w-0 lg:ml-56">
        <header className="lg:hidden sticky top-0 z-50 flex items-center gap-2 px-3 py-2 border-b border-white/10 bg-[#050505]/95 backdrop-blur-sm pt-[max(0.5rem,env(safe-area-inset-top))]">
          <button
            type="button"
            aria-label="Open menu"
            data-testid="admin-mobile-menu-btn"
            onClick={() => setMobileNavOpen(true)}
            className="inline-flex items-center justify-center min-h-11 min-w-11 -ml-1 text-white/80 hover:text-white hover:bg-white/5"
          >
            <Menu className="w-5 h-5" />
          </button>
          {chrome.backTo && (
            <button
              type="button"
              aria-label="Go back"
              onClick={() => navigate(chrome.backTo)}
              className="inline-flex items-center justify-center min-h-11 min-w-11 text-white/80 hover:text-white hover:bg-white/5"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
          )}
          <div className="min-w-0 flex-1">
            <p className="text-sm font-['Unbounded'] font-bold text-white truncate">{chrome.title}</p>
            <p className="text-[10px] font-['Manrope'] text-white/40">Drive Now Admin</p>
          </div>
        </header>
        <AdminPwaHead />
        <AdminPwaReloadPrompt />
        <AdminPwaInstallBanner />
        <main className="min-h-[100dvh] min-w-0">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
